import { makeAutoObservable, observable, runInAction } from 'mobx';
import type billingApiAgent from '../../services/api/billingApiAgent';
import type { TAdminCompany, TAdminCompanyDetail, TAdminCompanyErrorKind } from '../../services/models/BillingModels';
import { resolveErrorMessage } from '../../utils/resolveErrorMessage';
import type CompanyFieldStore from '../companyField/companyFieldStore';
import { groupOrganisationNumber } from '../purchase/organisationNumber';

export interface IAdminCompaniesStoreDependencies {
  /** The part of `billingApiAgent` the Companies tab uses: `Admin/Companies`. The register lookup is the company field's. */
  agent: Pick<typeof billingApiAgent, 'AdminCompanies'>;
  /** The Users tab's rows, the member search's source: `adminStore.getAllUsers`, wired in `createAdminCompaniesStore`. */
  loadUsers: () => Promise<IAdminUser[]>;
  /** The New company dialog's company field, with its own register lookup and clock: `createCompanyFieldStore`, wired in `createAdminCompaniesStore`. */
  newCompanyField: CompanyFieldStore;
}

/** A user as the Users tab lists them (`IUser` of `adminStore`), as far as the member search needs: `user_id` is the Auth0 user id. */
export interface IAdminUser {
  user_id: string;
  name: string;
  email: string;
}

/** A member of another company the superAdmin picked, waiting for a yes before they are moved: the backend's question, as it asked it. */
export interface IPendingMove {
  user: IAdminUser;
  message: string;
}

/** Our words for a Users list that could not be loaded and said nothing about why. */
export const USERS_UNAVAILABLE_MESSAGE = 'The users could not be loaded. Please try again.';

/** Our words for a list that could not be loaded and said nothing about why. */
export const COMPANIES_UNAVAILABLE_MESSAGE = 'The companies could not be loaded. Please try again.';

/**
 * A call the backend refused: its message, shown as sent, and its kind — the envelope's `reactName`,
 * or null when the call failed without one.
 */
export interface IAdminCompaniesFailure {
  message: string;
  kind: TAdminCompanyErrorKind | null;
}

/** The refusal an error carries. Read off the error rather than by its class, so the store need not load the request layer. */
export const failureOf = (error: unknown): IAdminCompaniesFailure => {
  const reactName = (error as { reactName?: unknown } | null)?.reactName;
  return { message: resolveErrorMessage(error), kind: typeof reactName === 'string' && reactName !== '' ? (reactName as TAdminCompanyErrorKind) : null };
};

/** What a refresh from the register did to the name: the page shows the new name, or "uendret". */
export type TNameRefresh = 'changed' | 'unchanged';

/** A company's list row: the company without its members. */
const rowOf = ({ members, profilesUnavailable, ...row }: TAdminCompanyDetail): TAdminCompany => row;

/**
 * The admin panel's Companies tab and company page: every company, the one open on its page, and
 * what a superAdmin does to them. The backend refuses in its own words, which are shown as sent.
 *
 * Imports no agent value, no store and no React; the billing agent, the Users list and the New company
 * dialog's company field are ports.
 */
export default class AdminCompaniesStore {
  // The list: every company and the search over it.

  /** Every company, open and closed, as the backend listed it. */
  companies: TAdminCompany[] = [];

  isLoadingList = false;

  /** Why the list could not be loaded, or null. */
  listError: string | null = null;

  /** What is in the search box: matched against the name, the organisation number (plain or grouped) and the invoice email. */
  search = '';

  // The New company dialog.

  /** True while the New company dialog is open. */
  isNewCompanyOpen = false;

  /** The New company dialog's company field: the purchase form's, over the same register lookup. */
  readonly newCompanyField: CompanyFieldStore;

  /** The New company dialog's invoice email, as typed. */
  newInvoiceEmail = '';

  isCreating = false;

  /** Why the backend refused the new company, in its words, or null. */
  createError: IAdminCompaniesFailure | null = null;

  // The company page: the open company and every change to it.

  /** The company open on its page, with its members, as the backend last answered it; null until one is opened, and after it is deleted. */
  company: TAdminCompanyDetail | null = null;

  isLoadingCompany = false;

  /** Why the company could not be opened, in the backend's words, or null. */
  companyError: IAdminCompaniesFailure | null = null;

  /** The invoice email field on the company page, as typed. */
  invoiceEmailDraft = '';

  /** True while a change to the open company is on its way. One at a time. */
  isWorking = false;

  /** Why the backend refused the latest change to the open company, in its words, or null. */
  actionError: IAdminCompaniesFailure | null = null;

  /** What the latest refresh from the register did to the name, or null when none has been made on this page. */
  nameRefresh: TNameRefresh | null = null;

  /** Raised by every opening. An answer lands only if the company it was asked for is still the one open. */
  private companyGeneration = 0;

  // The members: the member search, adding (and moving) a member, and removing one.

  /** The Users tab's rows, loaded once for the member search. */
  users: IAdminUser[] = [];

  isLoadingUsers = false;

  /** Why the Users list could not be loaded, or null. */
  usersError: string | null = null;

  /** What is in the Add member field: matched against the users' names and emails. */
  memberSearch = '';

  /** A move the backend asked about (`wouldMove`), waiting for a yes or a no; null when nothing is asked. */
  pendingMove: IPendingMove | null = null;

  private haveUsers = false;

  // The ports.

  private deps: IAdminCompaniesStoreDependencies;

  constructor(dependencies: IAdminCompaniesStoreDependencies) {
    this.deps = dependencies;
    this.newCompanyField = dependencies.newCompanyField;

    makeAutoObservable<this, 'deps' | 'companyGeneration' | 'haveUsers'>(this, {
      deps: false,
      companyGeneration: false,
      haveUsers: false,
      users: observable.ref,
      pendingMove: observable.ref,
      newCompanyField: false,
      companies: observable.ref,
      createError: observable.ref,
      company: observable.ref,
      companyError: observable.ref,
      actionError: observable.ref,
    });
  }

  // The list: every company and the search over it.

  /** The companies the search matches, in the backend's order; all of them while the search is empty. */
  get filteredCompanies(): TAdminCompany[] {
    const search = this.search.trim().toLowerCase();
    if (search === '') return this.companies;
    return this.companies.filter((company) =>
      [company.name, company.orgNumber, groupOrganisationNumber(company.orgNumber), company.invoiceEmail].some((value) => value.toLowerCase().includes(search)),
    );
  }

  setSearch = (value: string): void => {
    this.search = value;
  };

  /** Reads every company afresh. */
  loadCompanies = async (): Promise<void> => {
    this.isLoadingList = true;
    this.listError = null;
    try {
      const companies = await this.deps.agent.AdminCompanies.list();
      runInAction(() => {
        this.companies = companies;
        this.isLoadingList = false;
      });
    } catch (error) {
      runInAction(() => {
        this.isLoadingList = false;
        this.listError = resolveErrorMessage(error, COMPANIES_UNAVAILABLE_MESSAGE);
      });
    }
  };

  // The New company dialog.

  /** True when the new company can be saved: the company field resolved, an invoice email given, nothing on its way. */
  get canCreate(): boolean {
    return !this.isCreating && this.newCompanyField.resolvedCompany !== null && this.newInvoiceEmail.trim() !== '';
  }

  /** Opens the New company dialog empty. */
  openNewCompany = (): void => {
    this.newCompanyField.reset();
    this.newInvoiceEmail = '';
    this.createError = null;
    this.isNewCompanyOpen = true;
  };

  /** Closes the New company dialog, unless a company is being created. */
  closeNewCompany = (): void => {
    if (this.isCreating) return;
    this.isNewCompanyOpen = false;
  };

  setNewInvoiceEmail = (value: string): void => {
    this.newInvoiceEmail = value;
  };

  /**
   * Creates the company the field resolved to, with the invoice email: the resolved organisation
   * number is sent, never what was typed. On success it joins the list and the dialog closes; a
   * refusal stays on the dialog in the backend's words.
   */
  createCompany = async (): Promise<void> => {
    const company = this.newCompanyField.resolvedCompany;
    if (!this.canCreate || company === null) return;

    this.isCreating = true;
    this.createError = null;
    try {
      const created = await this.deps.agent.AdminCompanies.create({ orgNumber: company.orgNumber, invoiceEmail: this.newInvoiceEmail.trim() });
      runInAction(() => {
        this.isCreating = false;
        this.companies = [...this.companies.filter((row) => row.id !== created.id), rowOf(created)];
        this.isNewCompanyOpen = false;
      });
    } catch (error) {
      runInAction(() => {
        this.isCreating = false;
        this.createError = failureOf(error);
      });
    }
  };

  // The company page: the open company and every change to it.

  /** True when the invoice email field holds an address other than the one the company has, and nothing is on its way. */
  get canSaveInvoiceEmail(): boolean {
    const draft = this.invoiceEmailDraft.trim();
    return this.company !== null && !this.isWorking && draft !== '' && draft !== this.company.invoiceEmail;
  }

  /** Opens one company on its page, forgetting what the page showed for the one before. */
  openCompany = async (id: number): Promise<void> => {
    this.companyGeneration += 1;
    const generation = this.companyGeneration;
    this.company = null;
    this.companyError = null;
    this.actionError = null;
    this.nameRefresh = null;
    this.invoiceEmailDraft = '';
    this.memberSearch = '';
    this.pendingMove = null;
    this.isWorking = false;
    this.isLoadingCompany = true;
    try {
      const company = await this.deps.agent.AdminCompanies.get(id);
      runInAction(() => {
        if (generation !== this.companyGeneration) return;
        this.isLoadingCompany = false;
        this.show(company);
      });
    } catch (error) {
      runInAction(() => {
        if (generation !== this.companyGeneration) return;
        this.isLoadingCompany = false;
        this.companyError = failureOf(error);
      });
    }
  };

  setInvoiceEmailDraft = (value: string): void => {
    this.invoiceEmailDraft = value;
  };

  /** Saves the invoice email field, trimmed. A refusal keeps what was typed. */
  saveInvoiceEmail = async (): Promise<void> => {
    if (!this.canSaveInvoiceEmail) return;
    const invoiceEmail = this.invoiceEmailDraft.trim();
    await this.change((agent, id) => agent.setInvoiceEmail(id, invoiceEmail));
  };

  /** Asks the register for the company's name now; `nameRefresh` says whether it changed. */
  refreshName = async (): Promise<void> => {
    const before = this.company?.name;
    const refreshed = await this.change((agent, id) => agent.refreshName(id));
    if (refreshed === null) return;
    runInAction(() => {
      this.nameRefresh = refreshed.name === before ? 'unchanged' : 'changed';
    });
  };

  /** Closes the company; the backend closes its open memberships with it. Confirmed by the page first. */
  closeCompany = async (): Promise<void> => {
    await this.change((agent, id) => agent.close(id));
  };

  reopenCompany = async (): Promise<void> => {
    await this.change((agent, id) => agent.reopen(id));
  };

  /** Deletes the company. True when it is gone: off the list, and the page empty. Confirmed by the page first. */
  deleteCompany = async (): Promise<boolean> => {
    const id = this.company?.id;
    const gone = await this.change((agent, companyId) => agent.delete(companyId), { keep: false });
    if (gone === null || id === undefined) return false;
    runInAction(() => {
      this.companies = this.companies.filter((row) => row.id !== id);
    });
    return true;
  };

  /**
   * Sends one change to the open company and shows what the backend answers, on the page and in the
   * list. One change at a time; a refusal goes to `actionError`, unless `onRefusal` takes it (answers
   * true). Answers what the backend answered, or null when nothing was sent, the call was refused or
   * another company has been opened since.
   */
  private change = async (
    send: (agent: IAdminCompaniesStoreDependencies['agent']['AdminCompanies'], id: number) => Promise<TAdminCompanyDetail>,
    { keep = true, onRefusal }: { keep?: boolean; onRefusal?: (failure: IAdminCompaniesFailure) => boolean } = {},
  ): Promise<TAdminCompanyDetail | null> => {
    if (this.company === null || this.isWorking) return null;
    const generation = this.companyGeneration;
    this.isWorking = true;
    this.actionError = null;
    this.nameRefresh = null;
    try {
      const answered = await send(this.deps.agent.AdminCompanies, this.company.id);
      if (generation !== this.companyGeneration) return null;
      runInAction(() => {
        this.isWorking = false;
        if (keep) {
          this.show(answered);
        } else {
          this.company = null;
        }
      });
      return answered;
    } catch (error) {
      runInAction(() => {
        if (generation !== this.companyGeneration) return;
        this.isWorking = false;
        const failure = failureOf(error);
        if (onRefusal?.(failure)) return;
        this.actionError = failure;
      });
      return null;
    }
  };

  /** Shows a company as the backend answered it: on its page, with its invoice email in the field, and as its row in the list. */
  private show(company: TAdminCompanyDetail): void {
    this.company = company;
    this.invoiceEmailDraft = company.invoiceEmail;
    this.companies = this.companies.map((row) => (row.id === company.id ? rowOf(company) : row));
  }

  // The members: the member search, adding (and moving) a member, and removing one.

  /** The users the Add member field offers: those whose name or email holds what is typed, members of this company left out. None while nothing is typed. */
  get memberCandidates(): IAdminUser[] {
    const search = this.memberSearch.trim().toLowerCase();
    if (search === '') return [];
    const members = new Set((this.company?.members ?? []).map((member) => member.auth0UserId));
    return this.users.filter((user) => !members.has(user.user_id) && `${user.name ?? ''} ${user.email ?? ''}`.toLowerCase().includes(search));
  }

  setMemberSearch = (value: string): void => {
    this.memberSearch = value;
  };

  /** Loads the Users tab's rows for the member search, once; a failed load is tried again on the next call. */
  loadUsers = async (): Promise<void> => {
    if (this.haveUsers || this.isLoadingUsers) return;
    this.isLoadingUsers = true;
    this.usersError = null;
    try {
      const users = await this.deps.loadUsers();
      runInAction(() => {
        this.users = users;
        this.haveUsers = true;
        this.isLoadingUsers = false;
      });
    } catch (error) {
      runInAction(() => {
        this.isLoadingUsers = false;
        this.usersError = resolveErrorMessage(error, USERS_UNAVAILABLE_MESSAGE);
      });
    }
  };

  /**
   * Makes a picked user a member, by their Auth0 user id. A member of another company is not moved:
   * the backend's question (`wouldMove`) waits in `pendingMove` for `confirmMove` or `cancelMove`.
   * Every other refusal is shown as sent and not retried.
   */
  addMember = async (user: IAdminUser): Promise<void> => {
    const added = await this.change((agent, id) => agent.addMember(id, { auth0UserId: user.user_id, confirmMove: false }), {
      onRefusal: (failure) => {
        if (failure.kind !== 'wouldMove') return false;
        this.pendingMove = { user, message: failure.message };
        return true;
      },
    });
    if (added !== null) runInAction(() => (this.memberSearch = ''));
  };

  /** Yes to the question: moves the user from the other company to this one. */
  confirmMove = async (): Promise<void> => {
    const pending = this.pendingMove;
    if (pending === null) return;
    this.pendingMove = null;
    const added = await this.change((agent, id) => agent.addMember(id, { auth0UserId: pending.user.user_id, confirmMove: true }));
    if (added !== null) runInAction(() => (this.memberSearch = ''));
  };

  /** No to the question: the user stays where they are. */
  cancelMove = (): void => {
    this.pendingMove = null;
  };

  /** Ends a member's membership to the open company, by their owner id. Confirmed by the page first. */
  removeMember = async (ownerId: number): Promise<void> => {
    await this.change((agent, id) => agent.removeMember(id, ownerId));
  };
}
